import clsx from "clsx";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import salesChartLoading from "@assets/sales-v3-chart-loading.gif";
import { TitleSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { LabelWizardProvider } from "@features/breakdown-v2/label-rule-wizard/context";
import {
  DELETE_LABEL_RULE_GROUP_V3,
  DUPLICATE_LABEL_RULE_GROUP_V3,
  GET_LABEL_RULES_BY_GROUP_V3,
  GET_LABEL_RULE_GROUPS_V3,
  GET_LABEL_WIZARD_DATA_V3,
  PATCH_LABEL_RULE_GROUP_V3,
} from "@gql/label-rules-v3";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type DeleteLabelRuleGroupV3,
  type DeleteLabelRuleGroupV3Variables,
  type DuplicateLabelRuleGroupV3,
  type DuplicateLabelRuleGroupV3Variables,
  type GetLabelRulesByGroupV3,
  type GetLabelRulesByGroupV3Variables,
  type PatchLabelRuleGroupV3,
  type PatchLabelRuleGroupV3Variables,
} from "@north-beam/nb-client-side";
import { notifyLabelsCache } from "@shared/notify-labels-cache";

import { AdvancedEditor } from "./advanced-editor";
import { SimpleEditor } from "./simple-editor";

export const EditBreakdown = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { addToast } = useToasts();

  const { data, loading } = useNorthbeamQuery<
    GetLabelRulesByGroupV3,
    GetLabelRulesByGroupV3Variables
  >(GET_LABEL_RULES_BY_GROUP_V3, {
    useCpuIntensive: true,
    variables: { id: id ?? "" },
  });

  const [patchLabelRuleGroup, { loading: patching }] = useNorthbeamMutation<
    PatchLabelRuleGroupV3,
    PatchLabelRuleGroupV3Variables
  >(PATCH_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULES_BY_GROUP_V3, variables: { id: id ?? "" } },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const [duplicateLabelRuleGroup, { loading: duplicating }] =
    useNorthbeamMutation<
      DuplicateLabelRuleGroupV3,
      DuplicateLabelRuleGroupV3Variables
    >(DUPLICATE_LABEL_RULE_GROUP_V3, {
      useCpuIntensive: true,
      refetchQueries: [
        { query: GET_LABEL_RULE_GROUPS_V3 },
        { query: GET_LABEL_WIZARD_DATA_V3 },
      ],
      awaitRefetchQueries: true,
    });

  const [
    duplicateAndSwitchLabelRuleGroup,
    { loading: duplicatingAndSwitchingToAdvancedEditor },
  ] = useNorthbeamMutation<
    DuplicateLabelRuleGroupV3,
    DuplicateLabelRuleGroupV3Variables
  >(DUPLICATE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const [deleteLabelRuleGroup, { loading: deleting }] = useNorthbeamMutation<
    DeleteLabelRuleGroupV3,
    DeleteLabelRuleGroupV3Variables
  >(DELETE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  if (loading) {
    return (
      <div
        className={clsx(
          "bg-white border border-gray rounded-lg p-4",
          "h-[calc(100vh-56px-48px)]",
        )}
      >
        <TitleSlide>
          <img
            src={salesChartLoading}
            alt="Chart loading gif"
            className="w-1/3 m-auto"
          />
        </TitleSlide>
      </div>
    );
  }

  if (data?.me.labelRulesByGroupV3.length === 0) {
    return (
      <Navigate
        to={user.pathFromRoot(`/breakdowns${location.search}`)}
        replace={true}
      />
    );
  }

  const handleDuplicate = async () => {
    const labelRule = data?.me.labelRulesByGroupV3[0];

    prettyLogEventPage({
      domain: "Breakdown v2",
      action: "onClick",
      additionalActionInfo: "Duplicate breakdown",
      data: {
        pageContext: { labelRuleGroupId: labelRule?.groupId },
      },
    });

    if (labelRule) {
      const result = await duplicateLabelRuleGroup({
        variables: {
          id: labelRule.groupId,
          isUsingSimpleUI: labelRule.isUsingSimpleUI,
        },
      });

      addToast(
        `Breakdown "${result.data?.duplicateLabelRuleGroupV3.name}" created`,
        {
          appearance: "success",
        },
      );
      notifyLabelsCache();

      navigate(user.pathFromRoot(`/breakdowns${location.search}`));
    }
  };

  const handleDelete = async () => {
    const labelRule = data?.me.labelRulesByGroupV3[0];

    prettyLogEventPage({
      domain: "Breakdown v2",
      action: "onClick",
      additionalActionInfo: "Delete breakdown",
      data: {
        pageContext: { labelRuleGroupId: labelRule?.groupId },
      },
    });

    await deleteLabelRuleGroup({
      variables: { id: labelRule?.groupId ?? "" },
    });

    addToast(`Breakdown "${labelRule?.name}" deleted`, {
      appearance: "success",
    });
    notifyLabelsCache();

    navigate(user.pathFromRoot(`/breakdowns${location.search}`));
  };

  const isUsingSimpleUI = data?.me.labelRulesByGroupV3[0].isUsingSimpleUI;

  return (
    <LabelWizardProvider>
      {isUsingSimpleUI ? (
        <SimpleEditor
          deleting={deleting}
          duplicating={duplicating}
          duplicatingAndSwitchingToAdvancedEditor={
            duplicatingAndSwitchingToAdvancedEditor
          }
          switchingToAdvancedEditor={patching}
          labelRules={data!.me.labelRulesByGroupV3}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSwitchToAdvancedEditor={() => {
            const labelRule = data?.me.labelRulesByGroupV3[0];

            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Switch to advanced editor",
              data: {
                pageContext: { labelRuleGroupId: labelRule?.groupId },
              },
            });

            patchLabelRuleGroup({
              variables: { id: labelRule.groupId, isUsingSimpleUI: false },
            });
          }}
          onDuplicateAndSwitchToAdvancedEditor={async () => {
            const labelRule = data?.me.labelRulesByGroupV3[0];

            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Duplicate and switch to advanced editor",
              data: {
                pageContext: { labelRuleGroupId: labelRule?.groupId },
              },
            });

            const result = await duplicateAndSwitchLabelRuleGroup({
              variables: { id: labelRule.groupId, isUsingSimpleUI: false },
            });

            const record = result.data?.duplicateLabelRuleGroupV3;

            navigate(
              user.pathFromRoot(
                `/breakdowns/${record?.id}/edit${location.search}`,
              ),
            );
          }}
        />
      ) : (
        <AdvancedEditor
          deleting={deleting}
          duplicating={duplicating}
          labelRules={data!.me.labelRulesByGroupV3}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      )}
    </LabelWizardProvider>
  );
};
